'use client';

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type MouseEvent,
  type WheelEvent,
  type ReactNode,
} from 'react';

type WfNode = {
  id?: string;
  name: string;
  type: string;
  position?: [number, number];
  parameters?: Record<string, any>;
};

type NodeSummary = {
  type: string;
  displayName: string;
  icon: string;        // "fa:robot" | "file:google.svg"
  iconType: string;    // "icon" | "file"
  fileBuffer?: string; // base64 data URI
};

type ConnTarget = { node: string; type: string; index: number };
type WorkflowJson = {
  name?: string;
  nodes: WfNode[];
  connections: Record<string, { main?: ConnTarget[][]; [key: string]: ConnTarget[][] | undefined }>;
};

interface Props {
  workflowJson?: WorkflowJson | null;
  workflowJsonUrl?: string | null;
  workflowName?: string;
  nodesSummary?: NodeSummary[];
}

const NW = 100;
const NH = 100;
const PORT_R = 6;

// ─── Font Awesome icon paths (subset) ────────────────────────────
const FA_PATH: Record<string, string> = {
  'robot':        'M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7H3a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2zM7.5 14a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z',
  'database':     'M12 2C7.58 2 4 3.79 4 6v12c0 2.21 3.58 4 8 4s8-1.79 8-4V6c0-2.21-3.58-4-8-4zm0 2c3.87 0 6 1.5 6 2s-2.13 2-6 2-6-1.5-6-2 2.13-2 6-2zm6 12c0 .5-2.13 2-6 2s-6-1.5-6-2v-2.23C7.57 15.1 9.72 16 12 16s4.43-.9 6-2.23V16zm0-5c0 .5-2.13 2-6 2s-6-1.5-6-2V8.77C7.57 10.1 9.72 11 12 11s4.43-.9 6-2.23V11z',
  'comments':     'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z',
  'code':         'M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z',
  'filter':       'M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z',
  'globe':        'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z',
  'sticky-note':  'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z',
  'bolt':         'M7 2v11h3v9l7-12h-4l4-8z',
  'envelope':     'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z',
  'clock':        'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
  'sync':         'M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z',
  'link':         'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
  'play':         'M8 5v14l11-7z',
  'pause':        'M6 19h4V5H6v14zm8-14v14h4V5h-4z',
  'table':        'M20 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 2v3H5V5h15zm-9 5h-6v3h6v-3zm0 5h-6v3h6v-3zm8 3h-6v-3h6v3zm0-5h-6v-3h6v3z',
  'random':       'M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z',
  'cogs':         'M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.57 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.21.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z',
  'magic':        'M7.5 5.6L10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a1 1 0 0 0-1.41 0L1.29 18.96a1 1 0 0 0 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05a1 1 0 0 0 0-1.41l-2.33-2.35z',
};

const FA_COLOR: Record<string, string> = {
  'robot': '#ea580c', 'database': '#2563eb', 'comments': '#16a34a',
  'code': '#b45309', 'filter': '#7c3aed', 'globe': '#0891b2',
  'bolt': '#ef4444', 'envelope': '#dc2626', 'clock': '#16a34a',
  'sync': '#0891b2', 'table': '#2563eb', 'play': '#ea580c',
  'pause': '#991b1b', 'link': '#0891b2', 'magic': '#7c3aed',
  'cogs': '#6b7280', 'random': '#0891b2',
};

function renderFA(name: string, color: string) {
  const d = FA_PATH[name] || FA_PATH['code'];
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill={color || '#6b7280'}>
      <path d={d} />
    </svg>
  );
}

// ─── Fallback style by node type ─────────────────────────────────
type NStyle = { iconBg: string; iconColor: string; cardBorder: string; isTrigger: boolean };
function fallbackStyle(type: string): NStyle {
  const t = (type || '').replace('n8n-nodes-base.', '').replace('@n8n/n8n-nodes-langchain.', '').replace('@n8n/', '').toLowerCase();
  if (t.includes('trigger') || t.includes('webhook'))
    return { cardBorder: '#818cf8', iconBg: '#eef2ff', iconColor: '#4f46e5', isTrigger: true };
  if (t.includes('schedule') || t.includes('cron'))
    return { cardBorder: '#e5e7eb', iconBg: '#f0fdf4', iconColor: '#16a34a', isTrigger: true };
  if (t.includes('agent') || t.includes('openai') || t.includes('langchain'))
    return { cardBorder: '#e5e7eb', iconBg: '#f0fdf4', iconColor: '#16a34a', isTrigger: false };
  if (t.includes('slack') || t.includes('discord'))
    return { cardBorder: '#e5e7eb', iconBg: '#faf5ff', iconColor: '#7c3aed', isTrigger: false };
  if (t.includes('gmail') || t.includes('email'))
    return { cardBorder: '#e5e7eb', iconBg: '#fef2f2', iconColor: '#dc2626', isTrigger: false };
  if (t.includes('http') || t.includes('request'))
    return { cardBorder: '#e5e7eb', iconBg: '#ecfeff', iconColor: '#0891b2', isTrigger: false };
  if (t.includes('code') || t.includes('function'))
    return { cardBorder: '#e5e7eb', iconBg: '#fefce8', iconColor: '#b45309', isTrigger: false };
  if (t.includes('if') || t.includes('switch') || t.includes('filter'))
    return { cardBorder: '#e5e7eb', iconBg: '#faf5ff', iconColor: '#7c3aed', isTrigger: false };
  return { cardBorder: '#e5e7eb', iconBg: '#f3f4f6', iconColor: '#6b7280', isTrigger: false };
}

const STICKY_PALETTE = [
  { bg: '#fefce8', border: '#fde047', text: '#854d0e' },
  { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3' },
  { bg: '#fdf4ff', border: '#e9d5ff', text: '#7e22ce' },
  { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
];

const EDGE_LABELS: Record<string, string> = {
  ai_tool: 'tool', ai_memory: 'memory', ai_outputParser: 'parser',
  ai_document: 'doc', ai_embedding: 'embed', ai_retriever: 'retriever',
  ai_languageModel: 'model',
};

export default function WorkflowCanvas({
  workflowJson,
  workflowJsonUrl,
  nodesSummary = [],
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [remoteWorkflowJson, setRemoteWorkflowJson] = useState<WorkflowJson | null>(
    workflowJson || null
  );
  const [isLoadingJson, setIsLoadingJson] = useState(Boolean(!workflowJson && workflowJsonUrl));
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  useEffect(() => {
    if (workflowJson) {
      setRemoteWorkflowJson(workflowJson);
      setIsLoadingJson(false);
      return;
    }

    if (!workflowJsonUrl) {
      setRemoteWorkflowJson(null);
      setIsLoadingJson(false);
      return;
    }

    let cancelled = false;

    const loadWorkflowJson = async () => {
      setIsLoadingJson(true);

      try {
        const response = await fetch(workflowJsonUrl, { cache: 'force-cache' });
        if (!response.ok) {
          throw new Error(`Failed to fetch workflow JSON: ${response.status}`);
        }

        const data = await response.json();
        if (!cancelled) {
          setRemoteWorkflowJson(data);
        }
      } catch (error) {
        console.error('[WorkflowCanvas] Failed to load workflow JSON:', error);
        if (!cancelled) {
          setRemoteWorkflowJson(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingJson(false);
        }
      }
    };

    loadWorkflowJson();

    return () => {
      cancelled = true;
    };
  }, [workflowJson, workflowJsonUrl]);

  const resolvedWorkflowJson = workflowJson || remoteWorkflowJson;
  const allNodes: WfNode[] = resolvedWorkflowJson?.nodes || [];
  const connections = resolvedWorkflowJson?.connections || {};

  // type → summary lookup
  const summaryByType = new Map<string, NodeSummary>();
  nodesSummary.forEach(s => { if (s.type) summaryByType.set(s.type, s); });

  const stickyNodes = allNodes.filter(n => (n.type || '').toLowerCase().includes('stickynote'));
  const visibleNodes = allNodes.filter(n => !(n.type || '').toLowerCase().includes('stickynote'));
  const hasVisibleNodes = visibleNodes.length > 0;

  // auto-layout if no real positions (placeholder bounds when empty so hooks stay valid)
  const hasRealPositions =
    hasVisibleNodes &&
    visibleNodes.some(n => {
      const p = n.position;
      return p && (p[0] !== 0 || p[1] !== 0);
    });
  const layoutNodes: WfNode[] = !hasVisibleNodes
    ? []
    : hasRealPositions
      ? visibleNodes
      : visibleNodes.map((n, i) => {
          const COLS = Math.ceil(Math.sqrt(visibleNodes.length));
          return {
            ...n,
            position: [(i % COLS) * (NW + 80), Math.floor(i / COLS) * (NH + 80)] as [number, number],
          };
        });

  const PAD = 80;
  const allPos = layoutNodes.map(n => n.position || [0, 0]);
  const minX = hasVisibleNodes ? Math.min(...allPos.map(p => p[0])) - PAD : 0;
  const minY = hasVisibleNodes ? Math.min(...allPos.map(p => p[1])) - PAD : 0;
  const maxX = hasVisibleNodes ? Math.max(...allPos.map(p => p[0])) + NW + PAD : NW + PAD;
  const maxY = hasVisibleNodes ? Math.max(...allPos.map(p => p[1])) + NH + PAD : NH + PAD;
  const contentW = hasVisibleNodes ? Math.max(maxX - minX, NW * 3) : 300;
  const contentH = hasVisibleNodes ? Math.max(maxY - minY, NH * 2) : 200;

  const nodeByName = new Map<string, WfNode>();
  const nodeById = new Map<string, WfNode>();
  allNodes.forEach(n => { nodeByName.set(n.name, n); if (n.id) nodeById.set(n.id, n); });

  type Edge = { from: WfNode; to: WfNode; connType: string };
  const edges: Edge[] = [];
  Object.entries(connections).forEach(([srcKey, conn]) => {
    const from = nodeByName.get(srcKey) || nodeById.get(srcKey);
    if (!from) return;
    Object.entries(conn).forEach(([connType, groups]) => {
      (groups || []).forEach(group => {
        (group || []).forEach(c => {
          if (!c || c.node.startsWith('error-handler-')) return;
          const to = nodeByName.get(c.node) || nodeById.get(c.node);
          if (to && to !== from) edges.push({ from, to, connType });
        });
      });
    });
  });

  const fitToContainer = useCallback(() => {
    if (!hasVisibleNodes || !containerRef.current) return;
    const cw = containerRef.current.clientWidth;
    const ch = containerRef.current.clientHeight;
    if (!cw || !ch) return;
    const s = Math.min((cw - 40) / contentW, (ch - 40) / contentH, 1.5);
    setScale(s);
    setOffset({ x: (cw - contentW * s) / 2 - minX * s, y: (ch - contentH * s) / 2 - minY * s });
    setReady(true);
  }, [hasVisibleNodes, contentW, contentH, minX, minY]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !hasVisibleNodes) return;
    const ro = new ResizeObserver(fitToContainer);
    ro.observe(el);
    fitToContainer();
    return () => ro.disconnect();
  }, [fitToContainer, hasVisibleNodes]);

  const onMouseDown = (e: MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    setOffset({ x: dragStart.current.ox + e.clientX - dragStart.current.x, y: dragStart.current.oy + e.clientY - dragStart.current.y });
  };
  const onMouseUp = () => setIsDragging(false);
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    setScale(s => Math.min(Math.max(s * (e.deltaY > 0 ? 0.9 : 1.1), 0.1), 3));
  };

  function edgePath(from: WfNode, to: WfNode) {
    const [fx, fy] = from.position || [0, 0];
    const [tx, ty] = to.position || [0, 0];
    const x1 = fx - minX + NW;
    const y1 = fy - minY + NH / 2;
    const x2 = tx - minX;
    const y2 = ty - minY + NH / 2;
    const dx = Math.abs(x2 - x1) * 0.5 + 40;
    return { d: `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`, mx: (x1 + x2) / 2, my: (y1 + y2) / 2 };
  }

  if (isLoadingJson) {
    return (
      <div
        className="relative w-full rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
        style={{ height: 520 }}
      >
        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
          Loading workflow preview...
        </div>
      </div>
    );
  }

  if (!hasVisibleNodes) return null;

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-xl overflow-hidden border border-gray-200 select-none"
      style={{
        height: 520,
        cursor: isDragging ? 'grabbing' : 'grab',
        background: '#f5f5f5',
        backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 12px, rgba(0,0,0,0.03) 12px, rgba(0,0,0,0.03) 13px)',
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onWheel={onWheel}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0,
        transformOrigin: '0 0',
        transform: `translate(${offset.x}px,${offset.y}px) scale(${scale})`,
        opacity: ready ? 1 : 0,
      }}>

        {/* ── Sticky notes ── */}
        {stickyNodes.map((s, i) => {
          const [sx, sy] = s.position || [0, 0];
          const pal = STICKY_PALETTE[i % STICKY_PALETTE.length];
          const w = s.parameters?.width || 220;
          const h = s.parameters?.height || 160;
          const raw = (s.parameters?.content || '')
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
            .replace(/\*\*/g, '').replace(/^#+\s*/gm, '').trim();
          const lines = raw.split('\n').filter((l: string) => l.trim()).slice(0, 6);
          return (
            <div key={s.id || i} style={{
              position: 'absolute', left: sx - minX, top: sy - minY,
              width: w, height: h,
              background: pal.bg, border: `1.5px solid ${pal.border}`,
              borderRadius: 10, padding: '12px 14px',
              pointerEvents: 'none', overflow: 'hidden',
            }}>
              {lines[0] && <div style={{ fontSize: 12, fontWeight: 700, color: pal.text, marginBottom: 5 }}>{lines[0].slice(0, 45)}</div>}
              {lines.slice(1).map((l: string, j: number) => (
                <div key={j} style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>{l.slice(0, 55)}</div>
              ))}
            </div>
          );
        })}

        {/* ── SVG edges ── */}
        <svg style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible', pointerEvents: 'none' }}
          width={contentW} height={contentH}>
          <defs>
            <marker id="arr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0, 7 3.5, 0 7" fill="#c4c4c4" />
            </marker>
          </defs>
          {edges.map((e, i) => {
            const { d, mx, my } = edgePath(e.from, e.to);
            const [fx, fy] = e.from.position || [0, 0];
            const [tx, ty] = e.to.position || [0, 0];
            const label = EDGE_LABELS[e.connType];
            return (
              <g key={i}>
                <path d={d} fill="none" stroke="#c4c4c4" strokeWidth={2} markerEnd="url(#arr)" />
                <circle cx={fx - minX + NW} cy={fy - minY + NH / 2} r={PORT_R} fill="#fff" stroke="#c8c8c8" strokeWidth={1.5} />
                <circle cx={tx - minX} cy={ty - minY + NH / 2} r={PORT_R} fill="#fff" stroke="#c8c8c8" strokeWidth={1.5} />
                {label && (
                  <text x={mx} y={my - 6} textAnchor="middle" fontSize={10} fill="#9ca3af" fontFamily="system-ui,sans-serif">{label}</text>
                )}
              </g>
            );
          })}
        </svg>

        {/* ── Nodes ── */}
        {layoutNodes.map((node, i) => {
          const [nx, ny] = node.position || [0, 0];
          const summary = summaryByType.get(node.type);
          const style = fallbackStyle(node.type);
          const isTrigger = style.isTrigger || (node.type || '').toLowerCase().includes('trigger');

          let iconEl: ReactNode;
          if (summary?.iconType === 'file' && summary.fileBuffer) {
            iconEl = <img src={summary.fileBuffer} width={30} height={30} alt="" style={{ objectFit: 'contain' }} />;
          } else if (summary?.iconType === 'icon') {
            const faName = (summary.icon || '').replace(/^fa:/, '');
            const color = FA_COLOR[faName] || style.iconColor;
            iconEl = renderFA(faName, color);
          } else {
            iconEl = <span style={{ fontSize: 20, fontWeight: 700, color: style.iconColor }}>{node.name.slice(0, 1).toUpperCase()}</span>;
          }

          return (
            <div key={node.id || i} style={{
              position: 'absolute', left: nx - minX, top: ny - minY,
              width: NW, textAlign: 'center', pointerEvents: 'none',
            }}>
              <div style={{
                width: NW, height: NH, background: '#fff',
                border: `1.5px solid ${style.cardBorder}`,
                borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}>
                {isTrigger && (
                  <div style={{
                    position: 'absolute', top: -9, left: -9, width: 20, height: 20,
                    borderRadius: '50%', background: '#ef4444',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}>⚡</div>
                )}
                <div style={{
                  width: 58, height: 58, borderRadius: 12,
                  background: summary?.iconType === 'file' ? '#fff' : style.iconBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: summary?.iconType === 'file' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}>
                  {iconEl}
                </div>
              </div>
              {/* Use the instance name (user-defined, from workflow_json) */}
              <div style={{
                marginTop: 8, fontSize: 11.5, fontWeight: 500, color: '#374151',
                lineHeight: 1.3, maxWidth: NW + 30, marginLeft: -15, wordBreak: 'break-word',
              }}>
                {node.name.length > 26 ? node.name.slice(0, 24) + '…' : node.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Controls ── */}
      <div className="absolute bottom-3 left-3 flex gap-1 bg-white/95 rounded-lg p-1 shadow border border-gray-200">
        {([
          { icon: '⊡', title: 'Fit', fn: fitToContainer },
          { icon: '+', title: 'Zoom in', fn: () => setScale(s => Math.min(s * 1.25, 3)) },
          { icon: '−', title: 'Zoom out', fn: () => setScale(s => Math.max(s * 0.8, 0.1)) },
          { icon: '↺', title: 'Reset', fn: fitToContainer },
        ] as { icon: string; title: string; fn: () => void }[]).map(({ icon, title, fn }) => (
          <button key={icon} onClick={fn} title={title}
            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded text-sm font-medium transition-colors">
            {icon}
          </button>
        ))}
      </div>

      <div className="absolute top-3 right-3 flex gap-1.5">
        <span className="bg-white/90 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-400 shadow-sm">{visibleNodes.length} nodes</span>
        {edges.length > 0 && (
          <span className="bg-white/90 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-400 shadow-sm">{edges.length} edges</span>
        )}
      </div>
    </div>
  );
}
